import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PenLine, Type, Eraser, Stamp, Loader2, Check } from "lucide-react";

const STAMPS = [
  { v: "classic", label: "Classic", hint: "Signature over an accent rule" },
  { v: "minimal", label: "Minimal", hint: "Signature and text only" },
  { v: "seal", label: "Seal", hint: "Dashed frame" },
  { v: "box", label: "Box", hint: "Solid frame" },
];
const FRAMES = [["auto", "Auto"], ["none", "None"], ["dashed", "Dashed"], ["solid", "Solid"]];
const STAMP_FONTS = [["helv", "Helvetica"], ["times", "Times"], ["cour", "Courier"]];
const SIZES = [["compact", "Compact"], ["regular", "Regular"], ["large", "Large"]];
const LINES = [["show_title", "Title"], ["show_time", "Date & time"], ["show_key", "Approval key"], ["show_total", "Bid total (internal cover)"], ["show_fingerprint", "Fingerprint (internal cover)"], ["show_company", "\"Electronically signed\" line"]];
const INKS = [["#003E87", "Navy"], ["#111827", "Black"], ["#1D4ED8", "Blue"], ["#0F766E", "Teal"], ["#7C2D12", "Sepia"], ["#7F1D1D", "Burgundy"], ["#4C1D95", "Violet"], ["#065F46", "Green"]];
const ACCENTS = [["#F4C400", "Gold"], ["#003E87", "Navy"], ["#94A3B8", "Slate"], ["#B45309", "Bronze"], ["#DC2626", "Red"], ["#059669", "Green"]];
const PENS = [[2.2, "Fine"], [3.2, "Regular"], [4.6, "Bold"]];
const STAMP_DEFAULTS = { style: "classic", accent: "#F4C400", ink: "#003E87", text: "#262E38", frame: "auto", label: "Approved by", show_title: true, show_time: true, show_key: true, show_company: true, show_total: true, show_fingerprint: true, font: "helv", size: "regular" };

// Typed-signature faces: script hands first, then plain faces for people who sign in print.
export const SIG_FONTS = [
  { id: "caveat", label: "Caveat", css: "'Caveat', cursive", style: "italic", size: 64, kind: "script" },
  { id: "dancing", label: "Dancing Script", css: "'Dancing Script', cursive", style: "normal", size: 60, kind: "script" },
  { id: "great-vibes", label: "Great Vibes", css: "'Great Vibes', cursive", style: "normal", size: 66, kind: "script" },
  { id: "allura", label: "Allura", css: "'Allura', cursive", style: "normal", size: 68, kind: "script" },
  { id: "alex-brush", label: "Alex Brush", css: "'Alex Brush', cursive", style: "normal", size: 66, kind: "script" },
  { id: "sacramento", label: "Sacramento", css: "'Sacramento', cursive", style: "normal", size: 64, kind: "script" },
  { id: "homemade", label: "Homemade Apple", css: "'Homemade Apple', cursive", style: "normal", size: 44, kind: "script" },
  { id: "playfair", label: "Playfair", css: "'Playfair Display', serif", style: "italic", size: 48, kind: "plain" },
  { id: "cormorant", label: "Cormorant", css: "'Cormorant Garamond', serif", style: "normal", size: 52, kind: "plain" },
  { id: "inter", label: "Inter", css: "'Inter', sans-serif", style: "normal", size: 44, kind: "plain" },
];
const fontById = (id) => SIG_FONTS.find((f) => f.id === id) || SIG_FONTS[0];

const Swatches = ({ value, onChange, options, testid }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {options.map(([hex, name]) => (
      <button key={hex} type="button" title={name} data-testid={`${testid}-${name.toLowerCase()}`} onClick={() => onChange(hex)}
        className={`h-6 w-6 rounded-full border-2 grid place-items-center transition-transform hover:scale-110 ${value === hex ? "border-slate-900" : "border-white shadow"}`} style={{ background: hex }}>
        {value === hex && <Check className="w-3 h-3 text-white drop-shadow" />}
      </button>
    ))}
    <label className="relative h-6 w-6 rounded-full border-2 border-dashed border-slate-300 overflow-hidden cursor-pointer" title="Custom">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} className="absolute inset-0 opacity-0 cursor-pointer" data-testid={`${testid}-custom`} />
      <span className="absolute inset-0 grid place-items-center text-[10px] text-slate-400">+</span>
    </label>
  </div>
);

const Seg = ({ value, onChange, options, testid }) => (
  <div className="flex gap-1">
    {options.map(([v, l]) => (
      <button key={v} type="button" data-testid={`${testid}-${v}`} onClick={() => onChange(v)}
        className={`flex-1 rounded-md border px-2 py-1 text-[11px] ${value === v ? "border-[#003E87] bg-[#003E87]/5 text-[#003E87] font-semibold" : "border-slate-200 text-slate-600"}`}>{l}</button>
    ))}
  </div>
);

// Draw with a pointer, or type a name in a chosen hand and ink; tune the stamp; see the completed result rendered by the same code that stamps the PDFs.
export default function SignatureSetupDialog({ open, onClose, onSaved }) {
  const [mode, setMode] = useState("drawn");
  const [typed, setTyped] = useState("");
  const [font, setFont] = useState("caveat");
  const [ink, setInk] = useState("#003E87");
  const [pen, setPen] = useState(3.2);
  const [title, setTitle] = useState("");
  const [opts, setOpts] = useState(STAMP_DEFAULTS);
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState({ cover: null, acceptance: null, loading: false });
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const setOpt = (k, v) => setOpts((o) => ({ ...o, [k]: v }));

  useEffect(() => {
    if (!open) return;
    api.get("/me/signature").then((r) => {
      const s = r.data.signature;
      setExisting(s);
      if (s) {
        setMode(s.style || "drawn"); setTyped(s.typed_name || ""); setFont(s.typed_font || "caveat"); setInk(s.ink || "#003E87"); setTitle(s.title || "");
        setOpts({ ...STAMP_DEFAULTS, ...(s.stamp_opts || {}), style: s.stamp_opts?.style || s.stamp_style || "classic" });
      }
    }).catch(() => {});
    setDirty(false);
  }, [open]);

  useEffect(() => {
    if (!open || mode !== "typed") return;
    const c = canvasRef.current; if (!c) return;
    const f = fontById(font);
    let gone = false;
    const draw = () => {
      if (gone) return;
      const ctx = c.getContext("2d");
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.fillStyle = ink;
      let size = f.size;
      ctx.font = `${f.style} ${size}px ${f.css}`;
      while (size > 24 && ctx.measureText(typed || " ").width > c.width - 36) { size -= 2; ctx.font = `${f.style} ${size}px ${f.css}`; }
      ctx.textBaseline = "middle";
      ctx.fillText(typed || " ", 18, c.height / 2);
      setDirty(!!typed.trim());
    };
    const spec = `${f.style} ${f.size}px ${f.css}`;
    if (document.fonts?.load) document.fonts.load(spec, typed || "Signature").then(draw, draw); else draw();
    return () => { gone = true; };
  }, [typed, font, ink, mode, open]);

  const currentImage = useCallback(() => {
    const c = canvasRef.current;
    return dirty && c ? c.toDataURL("image/png") : (existing?.image || null);
  }, [dirty, existing]);

  // Live preview of the completed stamp (debounced): rendered server-side with sample approval data.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setPreview((p) => ({ ...p, loading: true }));
      const body = { image: currentImage(), stamp_opts: opts, title };
      const fetchOne = (where) => api.post("/me/signature/preview", { ...body, where }, { responseType: "blob" }).then((r) => URL.createObjectURL(r.data)).catch(() => null);
      const [cover, acceptance] = await Promise.all([fetchOne("cover"), fetchOne("acceptance")]);
      setPreview((p) => { if (p.cover) URL.revokeObjectURL(p.cover); if (p.acceptance) URL.revokeObjectURL(p.acceptance); return { cover, acceptance, loading: false }; });
    }, 450);
    return () => clearTimeout(t);
  }, [open, opts, title, typed, font, ink, dirty, mode, currentImage]);

  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); const sx = canvasRef.current.width / r.width; const sy = canvasRef.current.height / r.height; return [(e.clientX - r.left) * sx, (e.clientY - r.top) * sy]; };
  const down = (e) => { if (mode !== "drawn") return; drawing.current = true; const ctx = canvasRef.current.getContext("2d"); ctx.lineWidth = pen; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = ink; const [x, y] = pos(e); ctx.beginPath(); ctx.moveTo(x, y); canvasRef.current.setPointerCapture?.(e.pointerId); };
  const move = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext("2d"); const [x, y] = pos(e); ctx.lineTo(x, y); ctx.stroke(); setDirty(true); };
  const up = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setDirty(false); };

  const save = async () => {
    const image = currentImage();
    if (!image && !typed.trim()) { toast.error("Draw a signature or type your name"); return; }
    setBusy(true);
    try {
      const r = await api.put("/me/signature", { image, style: mode, typed_name: typed || undefined, typed_font: mode === "typed" ? font : undefined, ink, title: title || undefined, stamp_style: opts.style, stamp_opts: opts });
      toast.success("Signature and stamp saved");
      onSaved && onSaved(r.data.signature);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="signature-setup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Stamp className="w-4 h-4 text-[#003E87]" /> Your signature &amp; stamp</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Set up once. When you approve a bid, GridForge places this on the proposal&rsquo;s acceptance page and the internal estimate cover with your name, the time and the approval key.</DialogDescription>
        </DialogHeader>
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6">
          {/* ── Signature ───────────────────────────────────────────── */}
          <div className="space-y-3">
            <div className="flex gap-1 rounded-lg bg-slate-100 p-1 text-xs font-medium">
              <button data-testid="signature-mode-drawn" onClick={() => { setMode("drawn"); setTimeout(clear, 0); }} className={`flex-1 rounded-md px-2 py-1.5 flex items-center justify-center gap-1.5 ${mode === "drawn" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}><PenLine className="w-3.5 h-3.5" /> Draw it</button>
              <button data-testid="signature-mode-typed" onClick={() => setMode("typed")} className={`flex-1 rounded-md px-2 py-1.5 flex items-center justify-center gap-1.5 ${mode === "typed" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}><Type className="w-3.5 h-3.5" /> Type it</button>
            </div>
            {mode === "typed" && (
              <>
                <Input data-testid="signature-typed-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Your name as you sign it" />
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-xs text-slate-500">Hand</Label>
                    <span className="text-[11px] text-slate-400">Script faces first, plain faces after</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto pr-0.5" data-testid="signature-font-list">
                    {SIG_FONTS.map((f) => (
                      <button key={f.id} type="button" data-testid={`signature-font-${f.id}`} onClick={() => setFont(f.id)} title={f.label}
                        className={`rounded-md border px-2.5 py-1.5 text-left transition-colors ${font === f.id ? "border-[#003E87] bg-[#003E87]/5" : "border-slate-200 hover:border-slate-300"}`}>
                        <div className="truncate leading-none" style={{ color: ink, fontFamily: f.css, fontStyle: f.style, fontSize: f.kind === "script" ? 24 : 17, height: 28, display: "flex", alignItems: "center" }}>{typed.trim() || "Your Name"}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{f.label}{f.kind === "plain" ? " · plain" : ""}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div className="relative rounded-lg border border-dashed border-slate-300 bg-[linear-gradient(to_bottom,transparent_78%,#e2e8f0_78%,#e2e8f0_79%,transparent_79%)]">
              <canvas ref={canvasRef} width={640} height={200} data-testid="signature-canvas" className={`w-full h-[150px] touch-none ${mode === "drawn" ? "cursor-crosshair" : ""}`}
                onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} />
              {!dirty && existing?.image && mode === "drawn" && <img src={existing.image} alt="Current signature" className="absolute inset-0 w-full h-full object-contain pointer-events-none opacity-90" data-testid="signature-existing" />}
              {mode === "drawn" && <button onClick={clear} data-testid="signature-clear" className="absolute right-2 top-2 text-[11px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1"><Eraser className="w-3 h-3" /> Clear</button>}
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
              <div>
                <Label className="text-xs text-slate-500">Ink</Label>
                <div className="mt-1.5"><Swatches value={ink} onChange={setInk} options={INKS} testid="signature-ink" /></div>
              </div>
              {mode === "drawn" && (
                <div>
                  <Label className="text-xs text-slate-500">Pen</Label>
                  <div className="mt-1.5"><Seg value={pen} onChange={(v) => setPen(Number(v))} options={PENS} testid="signature-pen" /></div>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Title on the stamp</Label>
              <Input data-testid="signature-title-input" className="mt-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Owner, Lead Estimator" />
            </div>
          </div>

          {/* ── Stamp ───────────────────────────────────────────────── */}
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="stamp-preview">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wide text-slate-400">What it will look like</div>
                {preview.loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
              </div>
              <div>
                <div className="text-[10px] text-slate-400 mb-1">Internal estimate cover · full block</div>
                <div className="rounded border border-slate-200 bg-white p-2 min-h-[96px] grid place-items-center">
                  {preview.cover ? <img src={preview.cover} alt="Stamp preview, internal cover" className="max-h-40 w-auto" data-testid="stamp-preview-cover" /> : <span className="text-[11px] text-slate-400">Rendering…</span>}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-slate-400 mb-1">Proposal acceptance page · on the signature line</div>
                <div className="rounded border border-slate-200 bg-white p-2 min-h-[64px] grid place-items-center">
                  {preview.acceptance ? <img src={preview.acceptance} alt="Stamp preview, acceptance page" className="max-h-24 w-auto" data-testid="stamp-preview-acceptance" /> : <span className="text-[11px] text-slate-400">Rendering…</span>}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Stamp style</Label>
              <div className="mt-1 grid grid-cols-4 gap-1">
                {STAMPS.map((s) => (
                  <button key={s.v} title={s.hint} data-testid={`stamp-style-${s.v}`} onClick={() => setOpt("style", s.v)}
                    className={`rounded-md border px-2 py-1.5 text-xs ${opts.style === s.v ? "border-[#003E87] bg-[#003E87]/5 text-[#003E87] font-semibold" : "border-slate-200 text-slate-600"}`}>{s.label}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Frame</Label>
                <div className="mt-1"><Seg value={opts.frame} onChange={(v) => setOpt("frame", v)} options={FRAMES} testid="stamp-frame" /></div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Size</Label>
                <div className="mt-1"><Seg value={opts.size} onChange={(v) => setOpt("size", v)} options={SIZES} testid="stamp-size" /></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Heading & frame color</Label>
                <div className="mt-1.5"><Swatches value={opts.ink} onChange={(v) => setOpt("ink", v)} options={INKS} testid="stamp-ink" /></div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Accent rule</Label>
                <div className="mt-1.5"><Swatches value={opts.accent} onChange={(v) => setOpt("accent", v)} options={ACCENTS} testid="stamp-accent" /></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Text face</Label>
                <div className="mt-1"><Seg value={opts.font} onChange={(v) => setOpt("font", v)} options={STAMP_FONTS} testid="stamp-font" /></div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Heading word</Label>
                <Input data-testid="stamp-label-input" className="mt-1 h-8 text-sm" value={opts.label} maxLength={40} onChange={(e) => setOpt("label", e.target.value)} placeholder="Approved by" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Lines on the stamp</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1.5">
                {LINES.map(([k, l]) => (
                  <label key={k} className="flex items-center justify-between gap-2 text-xs text-slate-700">
                    <span className="truncate">{l}</span>
                    <Switch data-testid={`stamp-${k}`} checked={!!opts[k]} onCheckedChange={(v) => setOpt(k, v)} className="scale-90" />
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="text-[11px] text-slate-400" data-testid="signature-key">{existing?.key ? `Signature key ${existing.key} · set ${new Date(existing.set_at).toLocaleDateString()}` : ""}</div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button data-testid="signature-save-btn" disabled={busy} onClick={save} className="bg-[#003E87] hover:bg-[#00305F]">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save signature & stamp"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
